import React, { Component } from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import supercluster from 'supercluster';
import Loadable from 'react-loadable';
import pinSolid from '../assets/img/pin_solid.png';
import pinShadow from '../assets/img/pin_shadow.png';
import API from '../lib/api';
import slider from '../assets/js/index';

/**
 * Components that are loaded through code splitting
 */
const TotalAccidents = Loadable({
  loader: () => import('./TotalAccidents'),
  loading: () => null,
});

class Map extends Component {
  constructor(props) {
    super();
    const { vizParams } = props;
    /**
     * Map Bounds
     */
    this.southWest = L.latLng(vizParams.SW_Bounds);
    this.northEast = L.latLng(vizParams.NE_Bounds);

    this.state = {
      map: null,
      mapParams: {
        zoom: 10,
        minZoom: 10,
        maxZoom: 18,
        maxBounds: L.latLngBounds(this.southWest, this.northEast),
        center: vizParams.centerMap,
        zoomControl: false,
      },
    };
    this.index = null;
    this.markers = null;
    this.mapNode = React.createRef();
  }

  componentDidMount() {
    const { map } = this.state;
    if (!map) this.init(this.mapNode.current);
  }

  shouldComponentUpdate(nextProps) {
    const { incidentsData } = this.props;
    if (nextProps.incidentsData !== incidentsData) {
      return true;
    }
    return false;
  }

  componentDidUpdate() {
    const { incidentsData, updateLoading, vizParams } = this.props;
    const { map } = this.state;
    // Always clear if the incoming data change reset the map
    if (this.markers) {
      this.markers.clearLayers();
      map.setView(vizParams.centerMap, 10);
      this.index = null;
    }

    if (incidentsData) {
      /**
       * Timeout to give it some time to the interface to get ready
       * before start the super cluster
       */
      setTimeout(() => {
        this.initSuperCluster();
        // ----- finish the loading component
        updateLoading(false);
      }, 1000);
    }
  }

  getHasProperty = (data, property) => Object.prototype.hasOwnProperty.call(data, property)

  /**
   * Supercluster creator method, initialize the configuration of the cluster
   * and how is going to work
   */
  initSuperCluster = () => {
    const { map } = this.state;
    const { incidentsData } = this.props;

    // Validate if the data has the defined property
    if (typeof incidentsData !== 'undefined' && this.getHasProperty(incidentsData, 'features')) {
      // ----- Create the supercluster and bind the data
      this.markers = L.geoJSON(null, {
        pointToLayer: this.createClusterIcon,
      }).addTo(map);

      this.index = supercluster({
        radius: 170,
        extent: 512,
        maxZoom: 17,
        nodeSize: 128,
      }).load(incidentsData.features);
      this.markers.on('click', e => this.zoomCluster(e, map));
      this.updateMarkers();
    }
  }

  /**
   * Method that handle the zoom when the user click in a cluster
   */
  zoomCluster = (e, map) => {
    const clusterId = e.layer.feature.properties.cluster_id;
    const center = e.latlng;
    let expansionZoom;
    if (clusterId) {
      expansionZoom = this.index.getClusterExpansionZoom(clusterId);

      // ----- automatically zoom up one step more to display the markers that are together
      if (expansionZoom === 17) {
        expansionZoom += 1;
      }
      map.flyTo(center, expansionZoom);
    }
  }

  /**
   * Method that creates a custom marker icon
   */
  createIcon = () => {
    const pinIcon = L.icon({
      iconUrl: pinSolid,
      shadowUrl: pinShadow,
      iconSize: [30, 42], // size of the icon
      shadowSize: [32, 30], // size of the shadow
      iconAnchor: [16, 40], // point of the icon which will correspond to marker's location
      shadowAnchor: [2, 28], // the same for the shadow
      popupAnchor: [-1, -30], // point from which the popup should open relative to the iconAnchor
    });
    return pinIcon;
  }

  /**
   * When the data change, or the map has been zoom (in-out)  or dragged, update the markers
   * and the clusters visibles
   */
  updateMarkers = () => {
    const { map } = this.state;

    // Validate if the clusters are defined
    if (this.index) {
      const bounds = map.getBounds();
      const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
      const zoom = map.getZoom();

      const clusters = this.index.getClusters(bbox, zoom);
      this.markers.clearLayers();

      this.markers.addData(clusters);
    }
  }

  /**
   * When the user click a marker, the method fetch the 3rd party API of the slider
   * and replace the content of the popup with the data accident
   */
  updatePopUp = async (marker, data) => {
    const { properties, geometry } = data;
    const { vizParams } = this.props;
    const { REACT_APP_LOCATION } = process.env;

    // Star the async fetch of the data
    const popUp = marker.getPopup();
    const coord = geometry.coordinates;

    // Call the api to fetch the data of the popup and display it.
    const incidentData = await API.popUp.getIncidentData(properties.incident_id);

    // Get the date format
    const usDate = new Date(incidentData.properties.incident_date);

    // Date time format
    const l10nEN = new Intl.DateTimeFormat('en-US');
    const formatDate = l10nEN.format(usDate);
    const images = [];

    // Build the images according with the data provided
    let sliderData = [];
    let imagesContent = '';
    const defaultImg = 'https://bitbucket.org/netmidas/assets-crash-map/raw/184642620566e07028b281dad7e83cefb09b6235/default_image.jpg';

    /**
     * If the 3rd party API throw an error, catch the exception
     * and replace it with a default image
     */

    try {
      // Fetch of the images
      sliderData = await API.popUp.getSliderData([coord[1], coord[0]]);
      // console.log(sliderData);
      if (REACT_APP_LOCATION === 'corpus_christi') {
        images.push(sliderData);
      } else {
        sliderData.features.map((img) => {
          images.push(`https://d1cuyjsrcm0gby.cloudfront.net/${img.properties.key}/thumb-640.jpg`);
          return true;
        });
      }

      // create the images according to the amount fetched
      images.forEach((element) => {
        imagesContent += `<img class="mySlides" src=${element} onerror="this.onerror=null;this.src=${defaultImg};" style="width:100%"></img>`;
      });

      // If the API doesn't return images replace by the default image
      if (images.length === 0) {
        imagesContent += `<img class="mySlides" src=${defaultImg} style="width:100%"></img>`;
      }
    } catch (error) {
      imagesContent += `<img class="mySlides" src=${defaultImg} style="width:100%"></img>`;
    }

    // Set the popup content with the corresponding data
    popUp.setContent(`
      <div class="popup-container">
      
        <div class="w3-content w3-display-container ${vizParams.location === '0' ? 'slider__popup' : ''}">
          ${imagesContent}
          <button class="w3-button w3-light-grey w3-display-left ${vizParams.location === 'Corpus Christi' ? 'slider__popup' : ''}" onclick="handleSlide(-1)">&#10094;</button>
          <button class="w3-button  w3-light-grey w3-display-right ${vizParams.location === 'Corpus Christi' ? 'slider__popup' : ''}" onclick="handleSlide(1)">&#10095;</button>
        </div>

        <div class="popup-container__info ${vizParams.location === '0' ? 'popup-container__info--no__image' : ''}">
          <div class="popup-container__column-section">           
            <div class="popup-container__section">
              <i class="fas fa-briefcase-medical"></i>
              <p>Severity:</p>
              <p class="option__tag">${incidentData.properties.severity.length > 0 ? incidentData.properties.severity : 'Not Available'}</p>
            </div>

            <div class="popup-container__section">
              <i class="fas fa-puzzle-piece"></i>
              <p>Factors:</p>
              <p class="option__tag">${incidentData.properties.factors.length > 0 ? incidentData.properties.factors : 'Not Available'}</p>
            </div>

            <div class="popup-container__section">
              <i class="fas fa-sun"></i>
              <p>Weather:</p>
              <p class="option__tag">${incidentData.properties.weather_condition.length > 0 ? incidentData.properties.weather_condition : 'Not Available'}</p>
            </div>          
          </div>

          <div class="popup-container__separator"> </div>
                 
          <div class="popup-container__column-section">          
            <i class="fas fa-ambulance"></i>         
            <i class="fas fa-heartbeat"></i>       
            <i class="fas fa-calendar-alt"></i>   
          </div>

          <div class="popup-container__column-section">           
            <p>Injuries:</p>           
            <p>Fatalities:</p>           
            <p>Date:</p>           
          </div>

          <div class="popup-container__column-section">            
            <p class="option__tag right-col">${incidentData.properties.persons_injured}</p>
            <p class="option__tag right-col">${incidentData.properties.fatal_victims}</p>        
            <p class="option__tag right-col">${formatDate.length > 0 ? formatDate : 'Not Available'}</p>          
          </div>
        </div>

      </div>
    `);

    // Call the controller (external JS file) of the slider to set the initialImage
    try {
      slider(1);
    } catch (error) {
      return null;
    }
    return null;
  }

  /**
   * Create a corresponding circular-icon in each cluster according
   * with the amount of markers in each of them
   */
  createClusterIcon = (feature, latlng) => {
    const pinIcon = this.createIcon();
    const marker = L.marker(latlng, { icon: pinIcon });

    // Bind a popUp to each one of the individual markers
    const popUp = L.popup({
      autoPanPadding: [270, 280],
    }).setContent('Waiting data...');
    marker.bindPopup(popUp);
    // Trigger that handle the information of the popup
    marker.on('click', () => this.updatePopUp(marker, feature));

    if (!feature.properties.cluster) return marker;
    const count = feature.properties.point_count;

    // Define the style of the clusters
    let size = '';
    if (count < 2000) {
      size = 'small';
    } else if (count < 4000) {
      size = 'medium';
    } else {
      size = 'large';
    }

    const icon = L.divIcon({
      html: `<div class="leaflet-markercluster leaflet-markercluster--${size}"><b>${feature.properties.point_count_abbreviated}</b></div>`,
      iconSize: L.point(40, 40),
    });

    const markerToCluster = L.marker(latlng, {
      icon,
    });

    return markerToCluster;
  }

  /**
   * Method to start the Map rendering
   */
  init(id) {
    const { mapParams, map } = this.state;
    if (map) return;

    const { tileDesign, attribution } = this.props;

    // this function creates the Leaflet map object with the integration
    // of mapbox and is called after the Map component mounts
    const accessToken = 'pk.eyJ1IjoicGFvbGFnYyIsImEiOiJjam5nODFiYTQwMGUzM3dwa2V1cnRscXpyIn0.uLZMfVZJUnxTO7AyhquPvQ';
    const mapLeaf = L.map(id, mapParams);

    // Attribution layer
    const tileLayer = L.tileLayer(tileDesign, {
      id: 'mapbox.streets',
      attribution,
      accessToken,
    });

    tileLayer.addTo(mapLeaf);

    // Zoom control
    L.control
      .zoom({
        position: 'bottomleft',
      })
      .addTo(mapLeaf);

    // deactivate zoom events
    mapLeaf.touchZoom.disable();
    mapLeaf.doubleClickZoom.disable();
    mapLeaf.scrollWheelZoom.disable();
    mapLeaf.boxZoom.disable();
    mapLeaf.keyboard.disable();
    mapLeaf.invalidateSize();

    // Triggers to update the clusters
    mapLeaf.on('zoomend', () => this.updateMarkers());
    mapLeaf.on('dragend', () => this.updateMarkers());

    // set our state to include the map
    this.setState({ map: mapLeaf });
  }

  render() {
    return (
      <div className="map-wrapper">
        <div className="map-container">
          <TotalAccidents />
          <div ref={this.mapNode} id="map" />
        </div>
      </div>
    );
  }
}

Map.propTypes = {
  tileDesign: PropTypes.string,
  attribution: PropTypes.string,
  vizParams: PropTypes.objectOf(PropTypes.shape).isRequired,
  incidentsData: PropTypes.objectOf(PropTypes.shape),
  updateLoading: PropTypes.func.isRequired,
};

/**
 * Display the corresponding tile layer according with the environment
 */
const { NODE_ENV } = process.env;
/**
 * This accessToken depends on the viz, and is unique for each viz
 */
const accessToken = 'pk.eyJ1IjoicGFvbGFnYyIsImEiOiJjam5nODFiYTQwMGUzM3dwa2V1cnRscXpyIn0.uLZMfVZJUnxTO7AyhquPvQ';
let tileSource = '';

/**
 * Display the corresponding tile layer according with the environment
 */
if (NODE_ENV === 'development') {
  tileSource = 'http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
} else {
  tileSource = `https://api.mapbox.com/styles/v1/mapbox/streets-v10/tiles/256/{z}/{x}/{y}?access_token=${accessToken}`;
}

Map.defaultProps = {
  incidentsData: {},
  tileDesign: `${tileSource}`,
  attribution:
    '&copy; <a target="__blank" href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a target="__blank" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> | Data provided by <a target="__blank" href="https://www.txdot.gov/inside-txdot.html">Texas Department of Transportation</a> | Concept by <a href="https://datapico.com/">Datapico</a>',
};

export default Map;
